import { LogicService } from './logic.service';
import { Injectable } from '@angular/core';
import { Subscription, AsyncSubject, BehaviorSubject, Observable } from 'rxjs';
import { AngularFireAuth } from '@angular/fire/auth';
import { Router } from '@angular/router';
import { Employee } from '@akiba/models_bimbo';
import { DataSourceService } from './data-source.service';
import { environment } from '../../environments/environment';
import { Claims } from '../models/claims';
import { SsoAuthService } from './sso-auth.service';
import * as firebase from 'firebase';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {

  authSubscription?: Subscription = null;
  authEmployee?: Employee = null;
  authUserSubject: BehaviorSubject<Employee | null> = new BehaviorSubject(null);
  appLevel: Claims;
  href: string | null = null;
  expirationDate: Date;

  constructor(public fireAuth: AngularFireAuth, private router: Router, private dataService: DataSourceService, private sso: SsoAuthService, private logicService: LogicService) {
    this.authState();
  }

  authState() {
    this.authSubscription = this.fireAuth.authState.subscribe(auth => {
      if (auth) {

        const authJson: any = auth.toJSON();
        this.authEmployee = new Employee();
        this.authEmployee.uid = authJson.uid;

        this.fireAuth.auth.currentUser.getIdTokenResult().then(idTokenResult => {
          let date = new Date(idTokenResult.expirationTime);
          this.expirationDate = new Date(date.setDate(date.getDate() + 90));

          this.appLevel = new Claims(idTokenResult.claims);
          this.appLevel.environment = environment;

          if (this.appLevel.hasValidClaim()) {

            this.appLevel.loadAccessForClaim().then(() => {

              this.authUserSubject.next(this.authEmployee);

            }).catch(error => {
              console.log(error);
            });

          } else {

            this.signOut();

          }


        });

      } else {
        this.authUserSubject.next(null);
      }

    });

  }

  get isAuthenticated(): boolean {

    const isAuth = this.authEmployee !== null;
    return isAuth;
  }

  get currentUserObservable(): Observable<firebase.User> {
    return this.fireAuth.authState;
  }

  async signIn(credentials: any): Promise<firebase.auth.UserCredential | null> {
    const isIE = /*@cc_on!@*/false || !!window.document['documentMode'];

    if (credentials.email && credentials.secret) {

      return this.dataService.tokenForAdmin({ email: credentials.email, secret: credentials.secret }).then(result => {
        return this.fireAuth.auth.signInWithCustomToken(result.token);
      }).catch(error => {
        console.log(error);
        return null;
      });

    } else if (credentials.email && credentials.employeeNumber) {

      return this.dataService.tokenForSSOEmployee({ email: credentials.email, employeeNumber: credentials.employeeNumber }).then(result => {
        return this.fireAuth.auth.signInWithCustomToken(result.token);
      }).catch(error => {
        console.log(error);
        return null;
      });

    } else if (credentials.employeeNumber && credentials.password) {

      let userInfo: any = await this.dataService.loadEmployee(credentials.employeeNumber).pipe(take(1)).toPromise();
      let data: any = { employeeNumber: credentials.employeeNumber, password: credentials.password };
      if (credentials.seniorityDate) {
        data = { seniorityDate: credentials.seniorityDate, employeeNumber: credentials.employeeNumber, password: credentials.password };
      }


      return this.dataService.tokenForEmployee(data).then(async result => {
        if (!isIE){
          await this.fireAuth.auth.setPersistence(firebase.auth.Auth.Persistence.SESSION);
        }

        if (userInfo && userInfo.workInformation && userInfo.workInformation.laidOff) {
          return null;

        } else {
          return this.fireAuth.auth.signInWithCustomToken(result.token);
        }


      }).catch(error => {
        console.log(error);
        return null;
      });
    }
  }

  signOut() {
    this.authUserSubject.next(null);
    this.href = '';
    this.authEmployee = null;
    this.appLevel = null;
    this.expirationDate = null;
    this.fireAuth.auth.signOut();
    /** para IE */
    sessionStorage.clear();
    localStorage.clear();
    this.logicService.dataManager.principalDataLoader.reset();
    this.logicService.dataManager.proxyDataLoader.reset();
    this.router.navigate(['/mLogin']);

  }

  sendResetEmail(email: string): Promise<any> {
    return this.fireAuth.auth.sendPasswordResetEmail(email);
  }

}
